import { useCallback, useMemo, useState } from 'react'

import type { ShiftTotals } from '@krp/client-runtime'
import { toRubles } from '../../sale/model/money'

import type { AgentSession } from '../../agent-session/model/useAgentSession'
import type { OwnCashierSession } from '../../cashier/model/useOwnCashier'
import type { OwnCommandResult, OwnTotalsView } from '../ui/OwnActionsDialog'
import { isBlocked, tillCommands, type Blocked } from './commands'
import { commandResult, printResult, totalsFailure, totalsView } from './dialogs'

/**
 * Команды кассе из диалога «Действия» окна «Собственной»: смена, наличные, X-отчёт, копия чека, итоги смены. Каждая уходит только
 * по нажатию кассира и только с подтверждённым кассиром (`tillCommands`); итог команды и итоги смены — состояние диалога (память
 * окна), слова агента показываются дословно (ADR-8). Сумма наличных приходит копейками: агент принимает рубли.
 */

export interface OwnTill {
  /** Итоги смены для панели диалога. */
  totals: OwnTotalsView
  /** Итог последней команды; `null` — команды не было или диалог закрыт. */
  result: OwnCommandResult | null
  readTotals: () => void
  openShift: () => void
  closeShift: () => void
  cash: (type: 'in' | 'out', kopecks: number) => void
  printReportX: () => void
  printReceiptCopy: () => void
  recheck: () => void
  /** Диалог закрыт: итог команды забывается. */
  clearResult: () => void
}

export function useOwnTill(agent: AgentSession, cashier: Pick<OwnCashierSession, 'cashierNow' | 'blockReason'>): OwnTill {
  const { model, actions } = agent
  const { cashierNow, blockReason } = cashier
  const [result, setResult] = useState<OwnCommandResult | null>(null)
  const [totals, setTotals] = useState<{ data: ShiftTotals | null; loading: boolean; failed: boolean }>({ data: null, loading: false, failed: false })

  const readTotals = useCallback((): void => {
    setTotals((previous) => ({ ...previous, loading: true, failed: false }))
    void actions.shiftTotals().then((data) => setTotals({ data, loading: false, failed: data === null }))
  }, [actions])

  const commands = useMemo(() => tillCommands(actions, cashierNow, () => blockReason), [actions, blockReason, cashierNow])
  const blockedResult = (text: string): OwnCommandResult => ({ tone: 'warning', text, advice: null })

  const run = useCallback(
    (command: Promise<Parameters<typeof commandResult>[0] | Blocked>, doneText: string): void => {
      setResult(null)
      void command.then((verdict) => {
        if (isBlocked(verdict)) {
          setResult(blockedResult(verdict.blocked))
          return
        }
        setResult(commandResult(verdict, doneText, model))
        // Итоги и касса изменились: перечитать то, что окно показывает.
        void actions.recheck()
        if (verdict?.kind === 'sold') readTotals()
      })
    },
    [actions, model, readTotals],
  )

  return {
    totals: totals.failed ? totalsFailure(model) : totalsView(totals.data, totals.loading),
    result,
    readTotals,
    openShift: () => run(commands.openShift(), 'Смена открыта.'),
    closeShift: () => run(commands.closeShift(), 'Смена закрыта, Z-отчёт напечатан.'),
    cash: (type, kopecks) => run(commands.cash(type, toRubles(kopecks)), type === 'in' ? 'Наличные внесены.' : 'Наличные выплачены.'),
    printReportX: () => {
      setResult(null)
      void commands.printReportX().then((printed) => setResult(isBlocked(printed) ? blockedResult(printed.blocked) : printResult(printed, 'X-отчёт напечатан.', model)))
    },
    printReceiptCopy: () => {
      setResult(null)
      void actions.printLastReceiptCopy().then((printed) => setResult(printResult(printed, 'Копия чека напечатана.', model)))
    },
    recheck: () => void actions.recheck(),
    clearResult: () => setResult(null),
  }
}
